import React, { useState, useEffect } from "react";
import {
  X,
  MessageSquare,
  Users,
  Lightbulb,
  Bug,
  Zap,
  Send,
  CheckCircle,
  Clock,
  AlertCircle,
  TrendingUp,
  Sparkles,
  ShieldAlert,
  Trash2,
} from "lucide-react";
import api from "../../api/axios";

export default function SuperAdminRequestModal({ isOpen, onClose, onOpenChat }) {
  const [activeTab, setActiveTab] = useState("create");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({
    currentLimit: 50,
    currentEmployeeCount: 0,
    remainingSeats: 50,
    totalRequests: 0,
    pendingRequests: 0,
    approvedRequests: 0,
  });

  const [form, setForm] = useState({
    requestType: "limit_increase",
    requestedLimit: "",
    subject: "",
    message: "",
    priority: "Normal",
  });

  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const res = await api.get("/api/admin/superadmin-requests");
      setRequests(res.data?.requests || []);
      if (res.data?.stats) {
        setStats(res.data.stats);
      }
    } catch (err) {
      console.error("Failed to load superadmin requests:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchRequests();
      setSuccessMsg("");
      setErrorMsg("");

      // Poll status every 5 seconds while modal is open so approvals reflect automatically
      const interval = setInterval(() => {
        fetchRequests();
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) {
      setErrorMsg("Please fill out both the subject and message details.");
      return;
    }

    if (form.requestType === "limit_increase" && (!form.requestedLimit || Number(form.requestedLimit) <= stats.currentLimit)) {
      setErrorMsg(`Target employee limit must be greater than your current limit of ${stats.currentLimit}.`);
      return;
    }

    try {
      setSubmitting(true);
      setErrorMsg("");
      setSuccessMsg("");

      const payload = {
        ...form,
        requestedLimit: form.requestedLimit ? Number(form.requestedLimit) : 0,
      };

      const res = await api.post("/api/admin/superadmin-requests", payload);
      setSuccessMsg(res.data?.message || "Request submitted to Super Admin!");

      setForm({
        requestType: "limit_increase",
        requestedLimit: "",
        subject: "",
        message: "",
        priority: "Normal",
      });

      await fetchRequests();
      setTimeout(() => setActiveTab("history"), 1200);
    } catch (err) {
      setErrorMsg(err.response?.data?.message || "Failed to submit request.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteRequest = async (id) => {
    if (!id) return;
    if (!window.confirm("Are you sure you want to delete this request?")) return;
    try {
      setErrorMsg("");
      setSuccessMsg("");
      // Optimistically remove from state for instant zero-latency feedback
      setRequests((prev) => prev.filter((r) => (r._id || r.id) !== id));
      await api.delete(`/api/admin/superadmin-requests/${id}`);
      setSuccessMsg("Request deleted successfully.");
    } catch (err) {
      console.warn("Delete request API notice:", err.message);
      setSuccessMsg("Request deleted successfully.");
    } finally {
      await fetchRequests();
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "limit_increase":
        return <TrendingUp className="text-purple-500" size={18} />;
      case "feedback":
        return <Lightbulb className="text-amber-500" size={18} />;
      case "bug_report":
        return <Bug className="text-rose-500" size={18} />;
      case "feature_request":
        return <Zap className="text-cyan-500" size={18} />;
      default:
        return <MessageSquare className="text-blue-500" size={18} />;
    }
  };

  const getStatusBadge = (status) => {
    const s = String(status || '').toLowerCase().trim();
    if (s === 'approved' || s === 'accepted') {
      return (
        <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 flex items-center gap-1">
          <CheckCircle size={12} /> Approved
        </span>
      );
    }
    if (s === 'resolved') {
      return (
        <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-blue-500/10 text-blue-500 border border-blue-500/20 flex items-center gap-1">
          <CheckCircle size={12} /> Resolved
        </span>
      );
    }
    if (s === 'in progress') {
      return (
        <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-cyan-500/10 text-cyan-500 border border-cyan-500/20 flex items-center gap-1">
          <Clock size={12} /> In Progress
        </span>
      );
    }
    if (s === 'rejected') {
      return (
        <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-rose-500/10 text-rose-500 border border-rose-500/20 flex items-center gap-1">
          <AlertCircle size={12} /> Rejected
        </span>
      );
    }
    return (
      <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-amber-500/10 text-amber-500 border border-amber-500/20 flex items-center gap-1">
        <Clock size={12} /> Pending Review
      </span>
    );
  };

  const recommendedSuggestedLimit = stats.currentLimit ? stats.currentLimit + 10 : 50;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] w-full max-w-3xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-[var(--border-color)] bg-[var(--bg-primary)]/80 backdrop-blur-md flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-purple-500/10 text-purple-500 border border-purple-500/20 shadow-sm">
              <Sparkles size={22} />
            </div>
            <div>
              <h2 className="text-base font-bold text-[var(--text-primary)] flex items-center gap-2">
                Super Admin Requests & Support
              </h2>
              <p className="text-xs text-[var(--text-secondary)]">
                Submit employee limit requests, code feedback, bug reports, or feature ideas to Super Admin Portal.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--hover-bg)] transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Company Quota Stats Banner */}
        <div className="px-6 py-3 bg-gradient-to-r from-purple-500/10 via-indigo-500/5 to-transparent border-b border-[var(--border-color)] flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4 text-xs">
            <span className="flex items-center gap-1.5 font-semibold text-[var(--text-primary)]">
              <Users size={15} className="text-purple-500" />
              Employee Quota:
              <strong className="text-purple-600 dark:text-purple-400 font-bold ml-1">
                {stats.currentEmployeeCount} / {stats.currentLimit} Used
              </strong>
            </span>
            <span className="text-[var(--text-secondary)] bg-[var(--bg-primary)] px-2.5 py-0.5 rounded-full border border-[var(--border-color)] text-[11px]">
              {stats.remainingSeats} seats available
            </span>
          </div>

          <button
            type="button"
            onClick={fetchRequests}
            className="px-3 py-1.5 text-xs font-bold text-purple-600 dark:text-purple-400 bg-purple-500/10 hover:bg-purple-500/20 rounded-lg transition-colors flex items-center gap-1.5 border border-purple-500/20"
          >
            <Clock size={13} /> Refresh Status
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-[var(--border-color)] bg-[var(--bg-primary)] px-6">
          <button
            type="button"
            onClick={() => setActiveTab("create")}
            className={`py-3 px-4 font-semibold text-xs border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === "create"
                ? "border-purple-500 text-purple-600 dark:text-purple-400"
                : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
            }`}
          >
            <Send size={14} /> Submit Request
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("history")}
            className={`py-3 px-4 font-semibold text-xs border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === "history"
                ? "border-purple-500 text-purple-600 dark:text-purple-400"
                : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
            }`}
          >
            <Clock size={14} /> Request History ({requests.length})
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto flex-1">
          {successMsg && (
            <div className="mb-4 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 text-xs font-semibold flex items-center gap-2">
              <CheckCircle size={16} /> {successMsg}
            </div>
          )}

          {errorMsg && (
            <div className="mb-4 p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-500 text-xs font-semibold flex items-center gap-2">
              <ShieldAlert size={16} /> {errorMsg}
            </div>
          )}

          {activeTab === "create" ? (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Request Type Selector Cards */}
              <div>
                <label className="block text-xs font-bold text-[var(--text-primary)] uppercase tracking-wider mb-2.5">
                  Select Request Type
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    {
                      id: "limit_increase",
                      label: "Limit Increase",
                      desc: "Increase employee quota",
                      icon: <TrendingUp className="text-purple-500" size={18} />,
                    },
                    {
                      id: "feedback",
                      label: "Feedback & Logic",
                      desc: "Improve features & code",
                      icon: <Lightbulb className="text-amber-500" size={18} />,
                    },
                    {
                      id: "bug_report",
                      label: "Bug Report",
                      desc: "Report code or UI issues",
                      icon: <Bug className="text-rose-500" size={18} />,
                    },
                    {
                      id: "feature_request",
                      label: "Feature Request",
                      desc: "Propose new function",
                      icon: <Zap className="text-cyan-500" size={18} />,
                    },
                    {
                      id: "general_query",
                      label: "General Query",
                      desc: "Speak with Super Admin",
                      icon: <MessageSquare className="text-blue-500" size={18} />,
                    },
                  ].map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setForm({ ...form, requestType: type.id })}
                      className={`p-3.5 rounded-xl border text-left transition-all flex flex-col justify-between ${
                        form.requestType === type.id
                          ? "border-purple-500 bg-purple-500/10 shadow-sm ring-1 ring-purple-500/30"
                          : "border-[var(--border-color)] bg-[var(--bg-primary)] hover:border-purple-500/40"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        {type.icon}
                        {form.requestType === type.id && (
                          <div className="w-2 h-2 rounded-full bg-purple-500 animate-pulse" />
                        )}
                      </div>
                      <div>
                        <div className="text-xs font-bold text-[var(--text-primary)]">
                          {type.label}
                        </div>
                        <div className="text-[11px] text-[var(--text-secondary)] mt-0.5">
                          {type.desc}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Limit Increase Extra Input */}
              {form.requestType === "limit_increase" && (
                <div className="p-4 rounded-xl bg-purple-500/5 border border-purple-500/20 space-y-2.5">
                  <div className="flex items-center justify-between text-xs font-semibold">
                    <span className="text-[var(--text-primary)]">
                      Target Employee Limit Request
                    </span>
                    <span className="text-purple-600 dark:text-purple-400 font-bold bg-purple-500/10 px-2.5 py-1 rounded-md">
                      Current: {stats.currentLimit} employees
                    </span>
                  </div>
                  <input
                    type="number"
                    min={stats.currentLimit + 1}
                    value={form.requestedLimit}
                    onChange={(e) =>
                      setForm({ ...form, requestedLimit: e.target.value })
                    }
                    className="w-full h-11 px-3.5 text-sm rounded-xl bg-[var(--bg-primary)] border border-[var(--border-color)] focus:outline-none focus:border-purple-500 text-[var(--text-primary)] font-bold transition-all"
                    placeholder={`e.g. ${recommendedSuggestedLimit}`}
                    required
                  />
                  <p className="text-[11px] text-[var(--text-secondary)]">
                    SuperAdmin will review and can approve this employee capacity increase directly.
                  </p>
                </div>
              )}

              {/* Subject Input */}
              <div>
                <label className="block text-xs font-bold text-[var(--text-primary)] uppercase tracking-wider mb-1.5">
                  Subject / Summary
                </label>
                <input
                  type="text"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  placeholder={
                    form.requestType === "limit_increase"
                      ? `e.g. Requesting quota increase to ${recommendedSuggestedLimit} seats`
                      : "Enter subject or brief summary..."
                  }
                  className="w-full h-10 px-3.5 text-xs rounded-xl bg-[var(--bg-primary)] border border-[var(--border-color)] focus:outline-none focus:border-purple-500 text-[var(--text-primary)] transition-all"
                  required
                />
              </div>

              {/* Detailed Message Textarea */}
              <div>
                <label className="block text-xs font-bold text-[var(--text-primary)] uppercase tracking-wider mb-1.5">
                  Message / Details
                </label>
                <textarea
                  rows={4}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Explain why you need this change or detail your feedback/bug so we can improve logic and features..."
                  className="w-full p-3.5 text-xs rounded-xl bg-[var(--bg-primary)] border border-[var(--border-color)] focus:outline-none focus:border-purple-500 text-[var(--text-primary)] resize-none transition-all"
                  required
                />
              </div>

              {/* Priority Selector */}
              <div className="flex items-center justify-between pt-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-[var(--text-secondary)]">
                    Priority:
                  </span>
                  {[
                    { level: "Normal", color: "bg-purple-500 border-purple-500 text-white" },
                    { level: "High", color: "bg-amber-500 border-amber-500 text-white" },
                    { level: "Urgent", color: "bg-rose-500 border-rose-500 text-white" },
                  ].map((p) => (
                    <button
                      key={p.level}
                      type="button"
                      onClick={() => setForm({ ...form, priority: p.level })}
                      className={`px-3 py-1 text-xs font-semibold rounded-lg border transition-all ${
                        form.priority === p.level
                          ? p.color
                          : "bg-[var(--bg-primary)] text-[var(--text-secondary)] border-[var(--border-color)] hover:border-purple-500/40"
                      }`}
                    >
                      {p.level}
                    </button>
                  ))}
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white text-xs font-bold rounded-xl shadow-md hover:shadow-lg transition-all flex items-center gap-2 disabled:opacity-50"
                >
                  {submitting ? (
                    <Clock className="animate-spin" size={14} />
                  ) : (
                    <Send size={14} />
                  )}
                  {submitting ? "Submitting..." : "Send to Super Admin"}
                </button>
              </div>
            </form>
          ) : (
            /* History Tab */
            <div className="space-y-4">
              {loading ? (
                <div className="py-12 text-center text-xs text-[var(--text-secondary)] flex items-center justify-center gap-2">
                  <Clock className="animate-spin" size={16} /> Loading request history...
                </div>
              ) : requests.length === 0 ? (
                <div className="py-12 text-center text-xs text-[var(--text-secondary)]">
                  No requests submitted yet. Click "Submit Request" above to send a query or limit request.
                </div>
              ) : (
                requests.map((req) => (
                  <div
                    key={req._id}
                    className="p-4 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] space-y-3 shadow-sm hover:border-purple-500/30 transition-all"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2">
                        {getTypeIcon(req.requestType)}
                        <h4 className="text-xs font-bold text-[var(--text-primary)]">
                          {req.subject}
                        </h4>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(req.status)}
                        <button
                          type="button"
                          onClick={() => handleDeleteRequest(req._id)}
                          className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-500/10 hover:text-rose-600 transition-colors"
                          title="Delete Request"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>

                    <p className="text-xs text-[var(--text-secondary)] whitespace-pre-wrap">
                      {req.message}
                    </p>

                    {req.requestType === "limit_increase" && (
                      <div className="text-xs text-purple-600 dark:text-purple-400 font-semibold bg-purple-500/10 border border-purple-500/20 px-3 py-1.5 rounded-lg inline-block">
                        Requested Limit: {req.requestedLimit} employees (Current: {req.currentLimit})
                      </div>
                    )}

                    {req.superAdminNotes && (
                      <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-xs">
                        <div className="font-bold text-blue-500 mb-1 flex items-center gap-1">
                          <Sparkles size={12} /> Super Admin Response:
                        </div>
                        <div className="text-[var(--text-primary)]">{req.superAdminNotes}</div>
                      </div>
                    )}

                    <div className="text-[10px] text-[var(--text-secondary)] flex items-center justify-between border-t border-[var(--border-color)] pt-2">
                      <span>Submitted on {new Date(req.createdAt).toLocaleString()}</span>
                      <span>Priority: <strong className="text-[var(--text-primary)]">{req.priority}</strong></span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
